import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// #165 旋转粒子场 3D (Rotating Particle Field 3D)
export const RotatingParticleField3D: React.FC<{
  title?: string;
  particleCount?: number;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}> = ({
  title = 'PARTICLE FIELD',
  particleCount = 200,
  primaryColor = '#00d9ff',
  secondaryColor = '#ff006e',
  backgroundColor = '#0a0a0a',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const centerX = width / 2;
  const centerY = height / 2;

  // 生成 3D 粒子
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const seed = random(`particle-${i}`);
    const seed2 = random(`particle-alt-${i}`);

    // 初始位置（球形分布）
    const theta = seed * Math.PI * 2;
    const phi = seed2 * Math.PI;
    const radius = 300 + seed * 100;

    // 旋转动画
    const rotationY = frame * 0.02;
    const rotationX = Math.sin(frame * 0.01) * 0.2;

    // 3D 坐标
    const x = radius * Math.sin(phi) * Math.cos(theta + rotationY);
    const y = radius * Math.cos(phi) * Math.cos(rotationX);
    const z = radius * Math.sin(phi) * Math.sin(theta + rotationY);

    // 投影到 2D
    const perspective = 800;
    const scale = perspective / (perspective + z);
    const screenX = centerX + x * scale;
    const screenY = centerY + y * scale;

    // 颜色基于深度
    const depth = (z + radius) / (radius * 2);
    const hue = interpolate(depth, [0, 1], [180, 320]); // 青色到洋红色
    const color = `hsl(${hue}, 100%, 50%)`;

    // 大小基于深度
    const size = interpolate(depth, [0, 1], [1, 4]);

    return {
      x: screenX,
      y: screenY,
      size,
      opacity: interpolate(depth, [0, 1], [0.2, 1]),
      color,
    };
  });

  // 文字动画
  const textOpacity = interpolate(frame, [0, fps * 0.5], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* 粒子 */}
      {particles.map((particle, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: particle.x,
            top: particle.y,
            width: particle.size,
            height: particle.size,
            borderRadius: '50%',
            backgroundColor: particle.color,
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
          }}
        />
      ))}

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: textOpacity,
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 100,
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: `0 0 20px ${primaryColor}, 0 0 40px ${secondaryColor}`,
            fontFamily: 'Arial, sans-serif',
            textAlign: 'center',
          }}
        >
          {title}
        </div>
      </div>
    </AbsoluteFill>
  );
};
